import { http, HttpResponse } from 'msw';

const BASE = import.meta.env?.VITE_API_BASE_URL ?? 'http://localhost:8000';

let nextVenueId = 3;

let venueConnections: Array<Record<string, unknown>> = [
  {
    id: 1,
    user_id: 42,
    name: 'Binance Paper Account',
    exchange: 'BINANCE',
    execution_mode: 'PAPER',
    is_active: true,
    created_at: '2026-01-15T10:00:00Z',
    updated_at: '2026-03-01T08:30:00Z',
  },
  {
    id: 2,
    user_id: 42,
    name: 'OKX Paper Account',
    exchange: 'OKX',
    execution_mode: 'PAPER',
    is_active: true,
    created_at: '2026-02-01T14:00:00Z',
    updated_at: '2026-03-05T11:15:00Z',
  },
];

export const demoVenueConnectionsHandlers = [
  // ── List venue connections ────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/venue-connections`, () => {
    return HttpResponse.json({
      items: venueConnections,
      total: venueConnections.length,
      limit: 50,
      offset: 0,
    });
  }),

  // ── Create venue connection ───────────────────────────────────────────────
  http.post(`${BASE}/api/v1/venue-connections`, async ({ request }) => {
    const body = (await request.json()) as Record<string, unknown>;
    const created = {
      id: nextVenueId++,
      user_id: 42,
      name: (body['name'] as string) ?? 'New Connection',
      exchange: (body['exchange'] as string) ?? 'BINANCE',
      execution_mode: (body['execution_mode'] as string) ?? 'PAPER',
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    venueConnections = [...venueConnections, created];
    return HttpResponse.json(created, { status: 201 });
  }),

  // ── Get venue connection ──────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/venue-connections/:id`, ({ params }) => {
    const id = Number(params['id']);
    const conn = venueConnections.find((c) => c.id === id);
    if (!conn) return HttpResponse.json({ detail: 'Venue connection not found' }, { status: 404 });
    return HttpResponse.json(conn);
  }),

  // ── Update venue connection ───────────────────────────────────────────────
  http.patch(`${BASE}/api/v1/venue-connections/:id`, async ({ request, params }) => {
    const id = Number(params['id']);
    const body = (await request.json()) as object;
    venueConnections = venueConnections.map((c) =>
      c.id === id ? { ...c, ...body, updated_at: new Date().toISOString() } : c,
    );
    const updated = venueConnections.find((c) => c.id === id);
    return HttpResponse.json(updated);
  }),

  // ── Delete venue connection ───────────────────────────────────────────────
  http.delete(`${BASE}/api/v1/venue-connections/:id`, ({ params }) => {
    const id = Number(params['id']);
    venueConnections = venueConnections.filter((c) => c.id !== id);
    return new HttpResponse(null, { status: 204 });
  }),

  // ── Test connection ───────────────────────────────────────────────────────
  http.post(`${BASE}/api/v1/venue-connections/:id/test`, () => {
    return HttpResponse.json({ status: 'ok', latency_ms: 45 });
  }),

  // ── Balance ───────────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/venue-connections/:id/balance`, () => {
    return HttpResponse.json({
      balances: [
        { asset: 'USDT', free: '10000.00', locked: '500.00' },
        { asset: 'BTC', free: '0.45000000', locked: '0.05000000' },
        { asset: 'ETH', free: '5.20000000', locked: '0.00000000' },
      ],
    });
  }),

  // ── Symbols ───────────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/venue-connections/:id/symbols`, () => {
    return HttpResponse.json({
      symbols: [
        'BTCUSDT',
        'ETHUSDT',
        'SOLUSDT',
        'BNBUSDT',
        'XRPUSDT',
        'ADAUSDT',
        'DOGEUSDT',
        'AVAXUSDT',
        'DOTUSDT',
        'MATICUSDT',
      ],
    });
  }),

  // ── Config ────────────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/venue-connections/:id/config`, () => {
    return HttpResponse.json({ config: {} });
  }),

  // ── Live status ───────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/venue-connections/:id/live-status`, () => {
    return HttpResponse.json({ is_live: false, confirmed_at: null });
  }),

  // ── Confirm live ──────────────────────────────────────────────────────────
  http.post(`${BASE}/api/v1/venue-connections/:id/confirm-live`, () => {
    return HttpResponse.json({ is_live: true, confirmed_at: new Date().toISOString() });
  }),

  // ── Revoke live ───────────────────────────────────────────────────────────
  http.post(`${BASE}/api/v1/venue-connections/:id/revoke-live`, () => {
    return new HttpResponse(null, { status: 204 });
  }),

  // ── Live history ──────────────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/venue-connections/:id/live-history`, () => {
    return HttpResponse.json({ events: [] });
  }),

  // ── Key rotation: initiate ────────────────────────────────────────────────
  http.post(`${BASE}/api/v1/venue-connections/:id/rotate`, () => {
    return HttpResponse.json({ rotation_id: 'rot-demo-001', status: 'INITIATED' });
  }),

  // ── Key rotation: status ──────────────────────────────────────────────────
  http.get(`${BASE}/api/v1/venue-connections/:id/rotate/status`, () => {
    return HttpResponse.json({ rotation_id: 'rot-demo-001', status: 'AWAITING_NEW_KEYS' });
  }),

  // ── Key rotation: complete ────────────────────────────────────────────────
  http.post(`${BASE}/api/v1/venue-connections/:id/rotate/complete`, () => {
    return HttpResponse.json({ status: 'COMPLETED' });
  }),

  // ── Key rotation: abort ───────────────────────────────────────────────────
  http.post(`${BASE}/api/v1/venue-connections/:id/rotate/abort`, () => {
    return HttpResponse.json({ status: 'ABORTED' });
  }),
];
